import { useCallback, useEffect, useState } from 'react';

import { PromiseStatus } from '@/common/constants';
import { LockboxClaim } from '@/modules/employer/components/Lockbox/lockbox.types';
import { useGetClaimsRepaymentMatchesQueryAsync } from '@/modules/employer/components/Lockbox/queries/useGetClaimsRepaymentMatches.query';

interface Props {
  claims?: LockboxClaim[];
}

const useExtendClaimsWithMatchingData = ({ claims }: Props) => {
  const { getClaimsMatches } = useGetClaimsRepaymentMatchesQueryAsync();
  const [isLoaded, setIsLoaded] = useState(false);
  const [claimsWithMatchingData, setClaimsWithMatchingData] = useState<LockboxClaim[]>([]);

  const claimIds = claims?.map((claim) => claim.claimId).filter(Boolean) as string[];

  const claimKeys = claims
    ?.filter((claim) => claim?.claimId)
    ?.map((claim) => `${claim.claimId}_${claim.repaymentAmount}`) as string[];

  const loadClaimsWithMatchingData = useCallback(async (claimIds?: string[]) => {
    if (!claimIds || claimIds.length === 0) return;

    const claimsMatchingDataPromises = await Promise.allSettled(
      claimIds.map(async (claimId) => {
        try {
          const claimsMatchingData = await getClaimsMatches({ searchString: claimId });
          const match = claimsMatchingData.find((claimMatchingData) => claimMatchingData.claimId === claimId);
          return match;
        } catch {
          return null;
        }
      })
    );

    const enrichedData = claims?.map((claim) => {
      const matchResult = claimsMatchingDataPromises.find(
        (claimsMatchingDataPromise) =>
          claimsMatchingDataPromise.status === PromiseStatus.FULFILLED &&
          claimsMatchingDataPromise.value?.claimId === claim.claimId
      ) as PromiseFulfilledResult<LockboxClaim> | undefined;

      return {
        ...claim,
        ...(matchResult?.value || {}),
      };
    }) ?? [];

    setIsLoaded(true);
    setClaimsWithMatchingData(enrichedData);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [claims, getClaimsMatches, claimIds]);

  useEffect(() => {
    setIsLoaded(false);
    loadClaimsWithMatchingData(claimIds);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [JSON.stringify(claimKeys)]);

  return {
    claimsWithMatchingData,
    isLoaded,
  };
};

export default useExtendClaimsWithMatchingData;
